import { FormHelperText } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import WorkforceSuggestionComponent from "../../../generic/workforcesuggestion/WorkforceSuggestionComponent";
import { teamList } from "../../../../components/recoil/teamsRecoil";
import { useRecoilValue } from "recoil";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import apiService from "../../../../service/apiservice/apiService";

const isExistingMember = (existingMemberIds, userIdentifier) =>
  (existingMemberIds ?? []).some((id) => String(id) === String(userIdentifier));

const AddGroupMembersModal = ({
  isOpen,
  onClose,
  roomId,
  existingMemberIds = [],
  onMembersAdded,
}) => {
  const userJson = useReadMemory(MemoryType.USER);
  const teams = useRecoilValue(teamList);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const workforceEntityRecommenderPayloadData = useMemo(
    () => ({
      page: 1,
      organizationIdentifier: userJson?.organizationIdentifier,
      role: ["Volunteer", "Volunteer Coordinator"],
      teamIds: teams?.map((team) => team?.id),
      excludeIdentifiers: existingMemberIds,
    }),
    [existingMemberIds, teams, userJson?.organizationIdentifier],
  );

  const handleClose = useCallback(() => {
    setSelectedUsers([]);
    setError("");
    onClose();
  }, [onClose]);

  const handleDataChange = useCallback((data) => {
    setSelectedUsers(data.selectedUsers || []);
    setError("");
  }, []);

  const handleAddMembers = async () => {
    if (!roomId) {
      setError("Could not identify this group chat");
      return;
    }

    const usersToAdd = selectedUsers.filter(
      (user) =>
        user?.userIdentifier &&
        !isExistingMember(existingMemberIds, user.userIdentifier),
    );

    const payload = {
      roomId: Number(roomId),
      organizationIdentifier: userJson?.organizationIdentifier,
      updatedBy: userJson?.userIdentifier,
      addMemberUserIdentifiers: usersToAdd.map((user) => user.userIdentifier),
    };

    setIsAdding(true);
    try {
      const result = await apiService.post(
        "niramayafoundation/api/v1/communication/room",
        payload,
        "Members added",
        "Could not add members to the group",
      );

      if (!result) {
        return;
      }

      onMembersAdded?.(usersToAdd, result);
      handleClose();
    } finally {
      setIsAdding(false);
    }
  };

  const footer = (
    <div>
      <SecondaryButton buttonText="Cancel" handleOnClick={handleClose} buttonDisable={isAdding} />
      <PrimaryButton
        buttonText="Add Members"
        handleOnClick={handleAddMembers}
        loading={isAdding}
      />
    </div>
  );

  return (
    <CustomModal
      title="Add Members"
      isModalOpen={isOpen}
      closeModal={handleClose}
      footer={footer}
      closable
    >
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
      <WorkforceSuggestionComponent
        handleSelectedUserData={handleDataChange}
        workforceEntityRecommenderPayloadData={workforceEntityRecommenderPayloadData}
        showSelectAll
        isMultiSelect
      />
    </CustomModal>
  );
};

export default AddGroupMembersModal;
